import React, {useEffect, useState} from "react";
import {Link, NavLink, useLocation} from "react-router-dom";
import {ChevronDown, Menu, Phone, X} from "lucide-react";

import {
  coreServices as fallbackCoreServices,
  productBrands as fallbackProductBrands,
  siteInfo,
} from "../data";
import {getProductBrands} from "../cms/productQueries";
import {getServices} from "../cms/serviceQueries";

const navItems = [
  {label: "Home", to: "/"},
  {label: "About", to: "/about"},
  {label: "Services", to: "/services", type: "services"},
  {label: "Products", to: "/products", type: "products"},
  {label: "Industries", to: "/industries"},
  {label: "Contact", to: "/contact"},
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productBrands, setProductBrands] = useState(
    fallbackProductBrands,
  );
  const [coreServices, setCoreServices] = useState(fallbackCoreServices);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadBrands() {
      try {
        const cmsBrands = await getProductBrands();

        if (isMounted && Array.isArray(cmsBrands) && cmsBrands.length > 0) {
          setProductBrands(cmsBrands);
        }
      } catch (error) {
        console.error("Unable to load product brands from Sanity:", error);
      }
    }

    loadBrands();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadServices() {
      try {
        const cmsServices = await getServices();

        if (isMounted && Array.isArray(cmsServices) && cmsServices.length > 0) {
          setCoreServices(cmsServices);
        }
      } catch (error) {
        console.error("Unable to load services from Sanity:", error);
      }
    }

    loadServices();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setProductsOpen(false);
    setMobileServicesOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname, location.search]);

  const closeDesktopMenus = () => {
    setServicesOpen(false);
    setProductsOpen(false);
  };

  return (
    <>
      <div className="hidden md:block bg-[#0a1929] text-slate-200 text-[13px]">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-9">
          <span className="tracking-widest text-[11px] text-slate-400">
            Industrial Automation | PLC | SCADA | Robotics | Special Purpose Machines
          </span>

          <div className="flex items-center gap-6">
            <a
              href={`tel:${siteInfo.phone}`}
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              {siteInfo.phone}
            </a>

            <a
              href={`mailto:${siteInfo.email}`}
              className="hover:text-white transition-colors"
            >
              {siteInfo.email}
            </a>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 bg-white transition-shadow ${
          scrolled ? "shadow-md" : "shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img
              src={`${process.env.PUBLIC_URL || ""}/higa-logo.png`}
              alt="HIG Automation India"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const hasDropdown =
                item.type === "services" || item.type === "products";

              return (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => {
                    if (item.type === "services") {
                      setServicesOpen(true);
                      setProductsOpen(false);
                    }

                    if (item.type === "products") {
                      setProductsOpen(true);
                      setServicesOpen(false);
                    }
                  }}
                  onMouseLeave={() => {
                    if (item.type === "services") {
                      setServicesOpen(false);
                    }

                    if (item.type === "products") {
                      setProductsOpen(false);
                    }
                  }}
                >
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({isActive}) =>
                      `px-4 py-2 text-sm font-semibold tracking-wide flex items-center gap-1 transition-colors ${
                        isActive
                          ? "text-[#c8102e]"
                          : "text-[#0a1929] hover:text-[#c8102e]"
                      }`
                    }
                  >
                    {item.label}

                    {hasDropdown && (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                  </NavLink>

                  {item.type === "services" && servicesOpen && (
                    <div className="absolute top-full left-0 w-80 bg-white shadow-2xl border-t-4 border-[#c8102e] py-3 z-50">
                      <div className="px-5 pt-2 pb-3">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">
                          Engineering Services
                        </div>
                      </div>

                      {coreServices.map((service) => (
                        <Link
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          onClick={closeDesktopMenus}
                          className="block px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#c8102e] transition-colors"
                        >
                          {service.title}
                        </Link>
                      ))}

                      <div className="border-t border-slate-100 mt-3 pt-3 px-5">
                        <Link
                          to="/services"
                          onClick={closeDesktopMenus}
                          className="text-sm font-bold text-[#c8102e] hover:text-[#a80d26]"
                        >
                          View All Services →
                        </Link>
                      </div>
                    </div>
                  )}

                  {item.type === "products" && productsOpen && (
                    <div className="absolute top-full left-0 w-80 bg-white shadow-2xl border-t-4 border-[#c8102e] py-3 z-50">
                      <div className="px-5 pt-2 pb-3">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">
                          Product Brands
                        </div>
                      </div>

                      {productBrands.map((brand) => (
                        <Link
                          key={brand.slug}
                          to={`/products?category=${brand.slug}`}
                          onClick={closeDesktopMenus}
                          className="block px-5 py-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className="font-bold text-sm text-[#0a1929] uppercase tracking-wide hover:text-[#c8102e]">
                            {brand.title}
                          </div>

                          <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                            {brand.menuSummary}
                          </div>
                        </Link>
                      ))}

                      <div className="border-t border-slate-100 mt-3 pt-3 px-5">
                        <Link
                          to="/products"
                          onClick={closeDesktopMenus}
                          className="text-sm font-bold text-[#c8102e] hover:text-[#a80d26]"
                        >
                          View All Products →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <button
            type="button"
            className="lg:hidden p-2 text-[#0a1929]"
            onClick={() => setMobileOpen((current) => !current)}
            aria-label="Open navigation menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="flex flex-col py-2">
              <NavLink
                to="/"
                end
                className={({isActive}) =>
                  `block px-6 py-3 text-sm font-semibold border-l-4 ${
                    isActive
                      ? "border-[#c8102e] text-[#c8102e] bg-slate-50"
                      : "border-transparent text-[#0a1929]"
                  }`
                }
              >
                Home
              </NavLink>

              <NavLink
                to="/about"
                className={({isActive}) =>
                  `block px-6 py-3 text-sm font-semibold border-l-4 ${
                    isActive
                      ? "border-[#c8102e] text-[#c8102e] bg-slate-50"
                      : "border-transparent text-[#0a1929]"
                  }`
                }
              >
                About
              </NavLink>

              <div>
                <div className="flex items-center border-l-4 border-transparent">
                  <NavLink
                    to="/services"
                    className="flex-1 px-6 py-3 text-sm font-semibold text-[#0a1929]"
                  >
                    Services
                  </NavLink>

                  <button
                    type="button"
                    onClick={() =>
                      setMobileServicesOpen((current) => !current)
                    }
                    className="px-6 py-3 text-[#0a1929]"
                    aria-label="Show service list"
                    aria-expanded={mobileServicesOpen}
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        mobileServicesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {mobileServicesOpen && (
                  <div className="bg-slate-50 py-2">
                    {coreServices.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        className="block px-10 py-2.5 text-sm text-slate-600 hover:text-[#c8102e]"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center border-l-4 border-transparent">
                  <NavLink
                    to="/products"
                    className="flex-1 px-6 py-3 text-sm font-semibold text-[#0a1929]"
                  >
                    Products
                  </NavLink>

                  <button
                    type="button"
                    onClick={() =>
                      setMobileProductsOpen((current) => !current)
                    }
                    className="px-6 py-3 text-[#0a1929]"
                    aria-label="Show product brands"
                    aria-expanded={mobileProductsOpen}
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        mobileProductsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {mobileProductsOpen && (
                  <div className="bg-slate-50 py-2">
                    {productBrands.map((brand) => (
                      <Link
                        key={brand.slug}
                        to={`/products?category=${brand.slug}`}
                        className="block px-10 py-3 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="text-sm font-black uppercase tracking-wide text-[#0a1929]">
                          {brand.title}
                        </div>

                        <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                          {brand.menuSummary}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <NavLink
                to="/industries"
                className={({isActive}) =>
                  `block px-6 py-3 text-sm font-semibold border-l-4 ${
                    isActive
                      ? "border-[#c8102e] text-[#c8102e] bg-slate-50"
                      : "border-transparent text-[#0a1929]"
                  }`
                }
              >
                Industries
              </NavLink>

              <NavLink
                to="/contact"
                className={({isActive}) =>
                  `block px-6 py-3 text-sm font-semibold border-l-4 ${
                    isActive
                      ? "border-[#c8102e] text-[#c8102e] bg-slate-50"
                      : "border-transparent text-[#0a1929]"
                  }`
                }
              >
                Contact
              </NavLink>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
